import React, { useMemo } from 'react';

import {
  BooleanInput,
  ReferenceArrayInput,
  SelectArrayInput,
  useGetList,
} from 'react-admin';

import { Box } from '@mui/material';

import { capitalize } from 'lodash';
import { useWatch } from 'react-hook-form';

import entityTypes from '../../../../constants/entityTypes';
import SmartDateInput from '../../../../customFields/SmartDateInput';
import isPastDate from '../../../utils/reports/isPastDate';
import dateRangeValidation from '../../../utils/reports/dateRangeValidation';
import FieldBox from '../FieldBox';
import RangeFields from '../RangeFields';

const NO_GROUPS_ID = '__NO_GROUPS__';

type GroupChoice = {
  id: string;
  name: string;
  disabled?: boolean;
};

const parseGroupIds = (value?: string[]) => {
  if (!value?.length) {
    return undefined;
  }

  if (value.includes(NO_GROUPS_ID)) {
    return [];
  }

  return value;
};

const formatGroupIds = (value?: string[]) => {
  if (!Array.isArray(value)) {
    return [];
  }

  if (Array.isArray(value) && value.length === 0) {
    return [NO_GROUPS_ID];
  }

  return value;
};

const GroupsInput = () => {
  const watchedGroupIds = useWatch({ name: 'report.groupIds' });
  const selectedGroupIds = Array.isArray(watchedGroupIds) && watchedGroupIds.every((groupId) => typeof groupId === 'string')
    ? watchedGroupIds
    : undefined;
  const { data, isLoading } = useGetList<GroupChoice>('groups', {
    filter: { active: true },
    pagination: { page: 1, perPage: 200 },
  });

  const hasSelectedNoGroups = Array.isArray(selectedGroupIds) && selectedGroupIds.length === 0;
  const hasSelectedGroups = Array.isArray(selectedGroupIds) && selectedGroupIds.length > 0;

  const choices = useMemo<GroupChoice[]>(() => [
    {
      id: NO_GROUPS_ID,
      name: 'No groups',
      disabled: hasSelectedGroups,
    },
    ...(data ?? []).map((group) => ({
      id: group.id,
      name: group.name,
      disabled: hasSelectedNoGroups,
    })),
  ], [data, hasSelectedGroups, hasSelectedNoGroups]);

  if (isLoading) {
    return null;
  }

  return (
    <SelectArrayInput
      choices={choices}
      disableValue="disabled"
      fullWidth
      format={formatGroupIds}
      helperText={false}
      label=""
      optionText="name"
      parse={parseGroupIds}
      source="report.groupIds"
    />
  );
};

const EntitiesOverview = () => (
  <Box display="flex" flexDirection="column" gap={4}>
    <FieldBox title="Entity types" description="Filter to only include entities of these types. Leave empty to include all types (Individual, Business, Account, Product).">
      <SelectArrayInput
        choices={Object.values(entityTypes)
          .filter((type) => type !== entityTypes.CAR)
          .map(
            (status) => ({ id: status, name: capitalize(status) }),
          )}
        fullWidth
        label=""
        source="report.entityTypes"
        helperText={false}
      />
    </FieldBox>
    <FieldBox title="Groups" description="Only entities that are members of any of the selected groups are included. Leave empty to include entities regardless of group membership, or choose No groups to include only entities without any group memberships.">
      <GroupsInput />
    </FieldBox>
    <FieldBox title="Risk classes" description="Only entities assigned to any of the selected risk classes are included. Leave empty to include all entities.">
      <ReferenceArrayInput
        source="report.riskClassIds"
        reference="risk-classes"
        perPage={200}
      >
        <SelectArrayInput label="" helperText={false} />
      </ReferenceArrayInput>
    </FieldBox>
    <FieldBox title="Date range" description="Only entities created within this date range are included. Defaults to start of time through generation date.">
      <RangeFields
        from={(
          <SmartDateInput fullWidth validate={[isPastDate, dateRangeValidation]} source="report.dateFrom" label="From" helperText={false} />
        )}
        to={(
          <SmartDateInput fullWidth validate={[dateRangeValidation]} source="report.dateTo" label="To" helperText={false} />
        )}
      />
    </FieldBox>
    <FieldBox title="Include risk summary sheet" description="If enabled, include the Risk Summary sheet in the generated file." inline>
      <BooleanInput source="report.includeRiskSummary" label="" helperText={false} defaultValue={false} />
    </FieldBox>
  </Box>
);

export default EntitiesOverview;
